import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable, of } from 'rxjs'
import { HeaderActions } from './action-types'
import { catchError, concatMap, filter, map, switchMap, take } from 'rxjs/operators'
import { selectHeaderFilterCriteria } from './header.selectors'
import { PerfectScrollbarActions } from '../../../../shared/ui/perfect-scrollbar/state/actions-type'
import { StoreKeysEnum } from '../../../../store/store-keys'
import { NotificationControllerService } from '../../../../api/backend'
import { AuthContextStorageService } from '../../../../core/auth/auth-context-storage.service'
import { IHeaderFilterCriteria } from './header.reducers'

@Injectable()
export class HeaderEffects {

    filter$: Observable<any>

    dataLoaderAction$: Observable<any>
    fetchData$: Observable<any>
    filterCriteriaAction$: Observable<any>
    itemCollectionAction$: Observable<any>

    constructor(@Inject(Actions) private actions$: Actions,
                @Inject(Store) private store: Store,
                @Inject(NotificationControllerService) private notificationControllerService: NotificationControllerService,
                @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService
                ) {

        this.dataLoaderAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.fetchOriginData),
            filter((action) => {
                const [beforeColon] = action.key.split(':')
                return beforeColon === StoreKeysEnum.HeaderComponent
            }),
            concatMap((action) =>
                this.fetchData(action.key).pipe(
                    map(data => PerfectScrollbarActions.postLoadAction({ key: action.key, data })),
                    catchError(() => of(PerfectScrollbarActions.fetchError())),
                ),
            ),

        ))


        this.filterCriteriaAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.originFilterCriteriaAction),
            filter((action) => {
                const [beforeColon] = action.key.split(':')
                return beforeColon === StoreKeysEnum.HeaderComponent
            }),
            switchMap( (action) => {
                const [, afterColon] = action.key.split(':')
                return this.store.select(selectHeaderFilterCriteria(afterColon)).pipe(
                    take(1),
                    map((parcelFilterCriteria: IHeaderFilterCriteria) => {
                        const criteriaCopy = {...parcelFilterCriteria}

                        return HeaderActions.updateFilterCriteria({ key: afterColon, filterCriteriaFragment: { ...criteriaCopy, ...action.fragment } })
                    }))
            })
        ))

        this.itemCollectionAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.setOriginItemCollection),
            filter((action) => {
                const [beforeColon] = action.key.split(':')
                return beforeColon === StoreKeysEnum.HeaderComponent
            }),
            map( (action) => {
                return HeaderActions.setItemCollection({ key: action.key, itemCollection: action.collection })
            })
        ))

        this.filter$ = createEffect(() => this.actions$.pipe(
            ofType(HeaderActions.filter),
            switchMap((action) => {
                const [, afterColon] = action.key.split(':')
                return this.store.select(selectHeaderFilterCriteria(afterColon)).pipe(
                    take(1),
                    filter(filterCriteria => filterCriteria !== undefined && filterCriteria !== null),
                    concatMap(filterCriteria => {
                        filterCriteria.offset = 0

                        return [
                            HeaderActions.setFilterCriteria({
                                key: action.key,
                                criteria: filterCriteria
                            }),
                            PerfectScrollbarActions.filterChanged({
                                key: action.key,
                                initialLoad: false,
                                abortOriginFetch: true
                            })
                        ]
                    })
                )
            })
        ))
    }

    private fetchData(storeKey: string): Observable<any> {
        const [, afterColon] = storeKey.split(':')
        return this.store.select(selectHeaderFilterCriteria(afterColon)).pipe(
            take(1),
            switchMap((filterCriteria: IHeaderFilterCriteria) => {
                return this.notificationControllerService.getUserNotifications(
                    this.authContextStorageService.getProjectKey(),
                    filterCriteria.limit,
                    filterCriteria.offset,
                    filterCriteria.alreadyRead,
                    filterCriteria.lastYear,
                )
            })

        )

    }
}